import { useCallback, useState, useSyncExternalStore, type ReactNode } from 'react';
import type { BoardDoc } from '../doc/BoardDoc';
import {
  IconDots,
  IconDownload,
  IconFit,
  IconHome,
  IconHand,
  IconKanban,
  IconLayers,
  IconTable,
  IconTodo,
  IconPaperclip,
  IconMoon,
  IconNote,
  IconPen,
  IconScissors,
  IconShapes,
  IconFrame,
  IconText,
  IconRedo,
  IconSelect,
  IconSun,
  IconTitle,
  IconUndo,
  IconZoomIn,
  IconZoomOut,
  IconPlus,
  IconBoard,
  IconSheet,
} from '../ui/icons';
import { effectiveTheme, toggleTheme } from '../theme/theme';
import { resetRotation, selectAll, stressTest, zoomBy, zoomTo, zoomToFit, zoomToSelection } from './actions';
import { navigate, paths } from '../app/router';
import { useBoard, useBoardMeta, usePane, usePaneStore, useReadOnly } from './context';
import { importImageFiles, pickImageFiles } from './importImages';
import type { Tool } from './paneStore';
import { setToolSettings, useToolSettings } from './toolSettings';
import { flushThumbnails } from './thumbnail';
import { usePanel } from './panels';
import { ToolOptions } from './ToolOptions';
import { PaneMenu } from '../ui/PaneMenu';
import { useIsMobile } from '../ui/useMediaQuery';
import { useUpdate } from '../app/version';
import { setBoardPickerTarget } from './boardCard';
import { rasterStore } from './paint/rasterStore';
import { canRedoAction, canUndoAction, redoAction, sheetToolPressed, undoAction } from './paint/session';
import type { PaneStore } from './paneStore';

/** Board undo plus, while painting, the sheet's paint undo. */
function useUndoState(board: BoardDoc, pane: PaneStore): { canUndo: boolean; canRedo: boolean } {
  const store = rasterStore(board);
  const bits = useSyncExternalStore(
    (cb) => {
      board.undo.on('stack-item-added', cb);
      board.undo.on('stack-item-popped', cb);
      board.undo.on('stack-cleared', cb);
      const a = store.subscribe(cb);
      const b = pane.subscribe((s, prev) => {
        if (s.paintSheetId !== prev.paintSheetId || s.tool !== prev.tool) cb();
      });
      return () => {
        board.undo.off('stack-item-added', cb);
        board.undo.off('stack-item-popped', cb);
        board.undo.off('stack-cleared', cb);
        a();
        b();
      };
    },
    () => (canUndoAction({ board, pane }) ? 1 : 0) + (canRedoAction({ board, pane }) ? 2 : 0),
  );
  return { canUndo: (bits & 1) !== 0, canRedo: (bits & 2) !== 0 };
}

const CREATE_TOOLS: Tool[] = ['note', 'title', 'richtext', 'table', 'todo', 'kanban', 'shape', 'frame', 'cutout', 'sheet'];
const ADD_ITEMS: { tool: Tool; label: string; icon: () => React.JSX.Element }[] = [
  { tool: 'note', label: 'Sticky note', icon: IconNote },
  { tool: 'title', label: 'Title', icon: IconTitle },
  { tool: 'richtext', label: 'Text block', icon: IconText },
  { tool: 'table', label: 'Table', icon: IconTable },
  { tool: 'todo', label: 'To-do list', icon: IconTodo },
  { tool: 'kanban', label: 'Kanban', icon: IconKanban },
  { tool: 'shape', label: 'Shape (drag to size)', icon: IconShapes },
  { tool: 'frame', label: 'Frame (drag to size)', icon: IconFrame },
  { tool: 'cutout', label: 'Cut out from an image', icon: IconScissors },
  { tool: 'sheet', label: 'Sketch sheet (raster painting)', icon: IconSheet },
];

const DRAW_KEYS = 'P pen, U fineliner, B brush, M highlighter, G fill, E eraser, L lasso';

function ToolButton({
  tool,
  label,
  shortcut,
  children,
}: {
  tool: Tool;
  label: string;
  shortcut: string;
  children: ReactNode;
}) {
  const active = usePane((s) => s.tool === tool);
  const pane = usePaneStore();
  return (
    <button
      type="button"
      className={`tb-btn${active ? ' is-active' : ''}`}
      title={`${label} (${shortcut})`}
      aria-label={label}
      aria-pressed={active}
      onClick={() => pane.getState().setTool(tool)}
    >
      {children}
    </button>
  );
}

export function Toolbar() {
  const board = useBoard();
  const pane = usePaneStore();
  const ctx = { board, pane };
  const zoom = usePane((s) => s.camera.zoom);
  const rot = usePane((s) => s.camera.rot);
  const tool = usePane((s) => s.tool);
  const { canUndo, canRedo } = useUndoState(board, pane);
  const [theme, setTheme] = useState<'light' | 'dark'>(() => effectiveTheme());
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const menuOpen = menuAnchor !== null;
  const [addAnchor, setAddAnchor] = useState<HTMLElement | null>(null);
  const closeAdd = useCallback(() => setAddAnchor(null), []);
  const mobile = useIsMobile();
  const { panel, setPanel } = usePanel();
  const readOnly = useReadOnly();
  const isOwner = useBoardMeta().role === 'owner';
  const snap = useToolSettings((t) => t.snap);
  const snapGrid = useToolSettings((t) => t.snapGrid);
  const updateAvailable = useUpdate((s) => s.available !== null);

  const importImages = () => void pickImageFiles().then((files) => importImageFiles(ctx, files));
  const openBoardPicker = () => {
    setBoardPickerTarget(null);
    setPanel(panel === 'boards' ? null : 'boards');
  };

  const closeMenu = useCallback(() => setMenuAnchor(null), []);
  const menu = (action: () => void) => () => {
    setMenuAnchor(null);
    action();
  };

  return (
    <>
      <div className="toolbar" role="toolbar" aria-label="Tools">
      <a
        className="tb-btn"
        href={paths.dashboard}
        title="All boards"
        aria-label="All boards"
        onClick={(e) => {
          // plain click: push this board's thumbnail first so the dashboard shows the latest state
          if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
          e.preventDefault();
          void flushThumbnails().finally(() => navigate(paths.dashboard));
        }}
      >
        <IconHome />
      </a>
      <div className="sep" />
      <ToolButton tool="select" label="Select" shortcut="V">
        <IconSelect />
      </ToolButton>
      <ToolButton tool="pan" label="Pan" shortcut="H or Space">
        <IconHand />
      </ToolButton>
      {!readOnly && (
      <>
      <div className="sep" />
      {mobile ? (
        <>
          <ToolButton tool="draw" label="Draw" shortcut={DRAW_KEYS}>
            <IconPen />
          </ToolButton>
          <button
            type="button"
            className={`tb-btn${addAnchor || CREATE_TOOLS.includes(tool) ? ' is-active' : ''}`}
            title="Add"
            aria-label="Add an item"
            aria-expanded={!!addAnchor}
            onClick={(e) => setAddAnchor(addAnchor ? null : e.currentTarget)}
          >
            <IconPlus />
          </button>
          {addAnchor && (
            <PaneMenu anchor={addAnchor} placement="right" onRequestClose={closeAdd} className="add-menu">
              {ADD_ITEMS.map(({ tool: t, label, icon: Icon }) => (
                <button
                  key={t}
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setAddAnchor(null);
                    if (t === 'sheet') sheetToolPressed(ctx);
                    else pane.getState().setTool(t);
                  }}
                >
                  <span>
                    <Icon /> {label}
                  </span>
                </button>
              ))}
              <div className="sep" />
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  setAddAnchor(null);
                  importImages();
                }}
              >
                <span>
                  <IconPaperclip /> Import images / files
                </span>
              </button>
              {board.remote && (
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setAddAnchor(null);
                    openBoardPicker();
                  }}
                >
                  <span>
                    <IconBoard /> Board card
                  </span>
                </button>
              )}
            </PaneMenu>
          )}
        </>
      ) : (
        <>
      <ToolButton tool="note" label="Sticky note" shortcut="N">
        <IconNote />
      </ToolButton>
      <ToolButton tool="title" label="Title" shortcut="T">
        <IconTitle />
      </ToolButton>
      <ToolButton tool="richtext" label="Text block" shortcut="X">
        <IconText />
      </ToolButton>
      <ToolButton tool="table" label="Table" shortcut="Shift+B">
        <IconTable />
      </ToolButton>
      <ToolButton tool="todo" label="To-do list" shortcut="D">
        <IconTodo />
      </ToolButton>
      <ToolButton tool="kanban" label="Kanban" shortcut="K">
        <IconKanban />
      </ToolButton>
      <ToolButton tool="shape" label="Shape" shortcut="S">
        <IconShapes />
      </ToolButton>
      <ToolButton tool="frame" label="Frame (container: moving it carries what is inside)" shortcut="F">
        <IconFrame />
      </ToolButton>
      <ToolButton tool="draw" label="Draw" shortcut={DRAW_KEYS}>
        <IconPen />
      </ToolButton>
      <ToolButton tool="cutout" label="Cut out a piece of an image (lasso, rectangle or ellipse)" shortcut="C">
        <IconScissors />
      </ToolButton>
      <button
        type="button"
        className={`tb-btn${tool === 'sheet' ? ' is-active' : ''}`}
        title="Sketch sheet: paint the selected sheet, or place a new raster sheet (R)"
        aria-label="Sketch sheet"
        aria-pressed={tool === 'sheet'}
        onClick={() => sheetToolPressed(ctx)}
      >
        <IconSheet />
      </button>
      <button type="button" className="tb-btn" title="Import images or attach files (I)" aria-label="Import files" onClick={importImages}>
        <IconPaperclip />
      </button>
      {board.remote && (
        <button
          type="button"
          className={`tb-btn${panel === 'boards' ? ' is-active' : ''}`}
          title="Board card (embed another board)"
          aria-label="Board card"
          aria-pressed={panel === 'boards'}
          onClick={openBoardPicker}
        >
          <IconBoard />
        </button>
      )}
        </>
      )}
      <div className="sep" />
      <button
        type="button"
        className="tb-btn"
        title="Undo (Ctrl+Z)"
        aria-label="Undo"
        disabled={!canUndo}
        onClick={() => void undoAction(ctx)}
      >
        <IconUndo />
      </button>
      <button
        type="button"
        className="tb-btn"
        title="Redo (Ctrl+Shift+Z)"
        aria-label="Redo"
        disabled={!canRedo}
        onClick={() => void redoAction(ctx)}
      >
        <IconRedo />
      </button>
      </>
      )}
      <div className="sep" />
      {!mobile && (
      <>
      <button type="button" className="tb-btn" title="Zoom in (+)" aria-label="Zoom in" onClick={() => zoomBy(pane, 1.25)}>
        <IconZoomIn />
      </button>
      <button
        type="button"
        className="tb-btn tb-zoom"
        title="Reset zoom (0)"
        aria-label="Reset zoom"
        onClick={() => zoomTo(pane, 1)}
      >
        {Math.round(zoom * 100)}%
      </button>
      <button type="button" className="tb-btn" title="Zoom out (-)" aria-label="Zoom out" onClick={() => zoomBy(pane, 0.8)}>
        <IconZoomOut />
      </button>
      </>
      )}
      <button type="button" className="tb-btn" title="Zoom to fit (1)" aria-label="Zoom to fit" onClick={() => zoomToFit(ctx)}>
        <IconFit />
      </button>
      {rot !== 0 && (
        <button type="button" className="tb-btn tb-zoom" title="Reset canvas rotation" aria-label="Reset canvas rotation" onClick={() => resetRotation(pane)}>
          {Math.round(rot)}°
        </button>
      )}
      <div className="sep" />
      {!readOnly && !mobile && (
      <button
        type="button"
        className={`tb-btn${panel === 'layers' ? ' is-active' : ''}`}
        title="Layers"
        aria-label="Layers"
        aria-pressed={panel === 'layers'}
        onClick={() => setPanel(panel === 'layers' ? null : 'layers')}
      >
        <IconLayers />
      </button>
      )}
      {!mobile && (
      <button
        type="button"
        className="tb-btn"
        title="Toggle theme"
        aria-label="Toggle theme"
        onClick={() => setTheme(toggleTheme())}
      >
        {theme === 'dark' ? <IconSun /> : <IconMoon />}
      </button>
      )}
      <div className="tb-menu-host">
        <button
          type="button"
          className={`tb-btn${menuOpen ? ' is-active' : ''}`}
          title="More"
          aria-label="More"
          aria-expanded={menuOpen}
          onClick={(e) => setMenuAnchor(menuOpen ? null : e.currentTarget)}
        >
          <IconDots />
        </button>
        {menuAnchor && (
          <PaneMenu anchor={menuAnchor} placement="right" onRequestClose={closeMenu}>
            <button type="button" role="menuitem" onClick={menu(() => zoomToFit(ctx))}>
              <span>Zoom to fit</span>
              <kbd>1</kbd>
            </button>
            <button type="button" role="menuitem" onClick={menu(() => zoomToSelection(ctx))}>
              <span>Zoom to selection</span>
              <kbd>2</kbd>
            </button>
            <button type="button" role="menuitem" onClick={menu(() => selectAll(ctx))}>
              <span>Select all</span>
              <kbd>Ctrl+A</kbd>
            </button>
            {mobile && (
              <>
                <div className="sep" />
                {!readOnly && (
                  <button type="button" role="menuitem" onClick={menu(() => setPanel(panel === 'layers' ? null : 'layers'))}>
                    <span>Layers…</span>
                  </button>
                )}
                <button type="button" role="menuitem" onClick={menu(() => setTheme(toggleTheme()))}>
                  <span>{theme === 'dark' ? 'Light theme' : 'Dark theme'}</span>
                </button>
              </>
            )}
            {!readOnly && (
            <>
            <div className="sep" />
            <button type="button" role="menuitemcheckbox" aria-checked={snap} onClick={() => setToolSettings({ snap: !snap })}>
              <span>{snap ? '☑' : '☐'} Snap to objects</span>
              <kbd>'</kbd>
            </button>
            <button type="button" role="menuitemcheckbox" aria-checked={snapGrid} onClick={() => setToolSettings({ snapGrid: !snapGrid })}>
              <span>{snapGrid ? '☑' : '☐'} Snap to grid</span>
            </button>
            <div className="sep" />
            <button type="button" role="menuitem" onClick={menu(() => setPanel('settings'))}>
              <span>Board settings…</span>
            </button>
            <button type="button" role="menuitem" onClick={menu(() => setPanel('storage'))}>
              <span>Storage & imports…</span>
            </button>
            {isOwner && board.remote && (
              <button type="button" role="menuitem" onClick={menu(() => setPanel('share'))}>
                <span>Share…</span>
              </button>
            )}
            </>
            )}
            <button type="button" role="menuitem" onClick={menu(() => setPanel('export'))}>
              <span>Export… (PNG, ZIP backup)</span>
              <IconDownload />
            </button>
            {board.remote && (
              <button type="button" role="menuitem" onClick={menu(() => navigate(paths.board(board.boardId, board.boardId)))}>
                <span>Split view</span>
              </button>
            )}
            <button type="button" role="menuitem" onClick={menu(() => setPanel('shortcuts'))}>
              <span>Keyboard shortcuts</span>
              <kbd>?</kbd>
            </button>
            <button type="button" role="menuitem" onClick={menu(() => setPanel('about'))}>
              <span>{updateAvailable ? 'About & updates… (new version)' : 'About & updates…'}</span>
            </button>
            {!readOnly && (
            <>
            <div className="sep" />
            <button type="button" role="menuitem" onClick={menu(() => stressTest(ctx, 1000))}>
              <span>Add 1000 test notes</span>
              <kbd>dev</kbd>
            </button>
            <button
              type="button"
              role="menuitem"
              className="danger"
              onClick={menu(() => {
                if (confirm('Delete every item on this board?')) {
                  board.clear();
                  pane.getState().clearSelection();
                }
              })}
            >
              <span>Clear board</span>
            </button>
            </>
            )}
          </PaneMenu>
        )}
      </div>
      </div>
      <ToolOptions />
    </>
  );
}
